import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getPlatformClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { CreatePlatformUserDialog } from './admin-users';

export const Route = createFileRoute('/platform/organizations')({
  component: OrganizationsPage,
});

const PAGE_SIZE = 25;

function OrganizationsPage(): React.ReactElement {
  const [pickerOrgId, setPickerOrgId] = useState<string | null>(null);
  const [showAddPlatformUser, setShowAddPlatformUser] = useState(false);
  const [rawSearch, setRawSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  // Debounce search input 250ms so typing doesn't spam the API. Reset to
  // page 1 whenever the search term changes — matches user expectation
  // that a new query starts at the beginning.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(rawSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [rawSearch]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const orgs = useQuery({
    queryKey: queryKeys.platform.organizations({ q: debouncedSearch, page, pageSize: PAGE_SIZE }),
    queryFn: ({ signal }) =>
      getPlatformClient().listOrganizations(
        { q: debouncedSearch || undefined, page, pageSize: PAGE_SIZE },
        signal
      ),
    // Keep the previous page visible while a new page loads — avoids the
    // table flashing empty between pagination clicks.
    placeholderData: keepPreviousData,
  });

  const total = orgs.data?.total ?? 0;
  const totalPages = orgs.data?.totalPages ?? 1;
  const rows = orgs.data?.data ?? [];

  return (
    <div className="space-y-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Organizations</h2>
          <p className="text-muted-foreground text-sm">
            Every tenant on the platform. Click "Impersonate" to pick a user and act on their
            behalf.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowAddPlatformUser(true)}>
          Add platform user
        </Button>
      </header>

      <Input
        placeholder="Search by organization, user name, or email…"
        value={rawSearch}
        onChange={(e) => setRawSearch(e.target.value)}
        className="max-w-md"
      />

      {orgs.isLoading && !orgs.data && <div className="text-muted-foreground">Loading…</div>}
      {orgs.isError && <div className="text-destructive">Failed to load organizations.</div>}

      {orgs.data && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Active users</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    {debouncedSearch
                      ? `No organizations match "${debouncedSearch}".`
                      : 'No organizations yet.'}
                  </TableCell>
                </TableRow>
              )}
              {rows.map((org) => (
                <TableRow key={org.id}>
                  <TableCell className="font-medium">{org.name}</TableCell>
                  <TableCell>{org.activeUserCount}</TableCell>
                  <TableCell>{new Date(org.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => setPickerOrgId(org.id)}>
                      Impersonate
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {total === 0
                ? '0 results'
                : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total}`}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1 || orgs.isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span>
                Page {page} of {totalPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages || orgs.isFetching}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      {pickerOrgId && (
        <ImpersonationPicker organizationId={pickerOrgId} onClose={() => setPickerOrgId(null)} />
      )}

      {showAddPlatformUser && (
        <CreatePlatformUserDialog onClose={() => setShowAddPlatformUser(false)} />
      )}
    </div>
  );
}

function ImpersonationPicker({
  organizationId,
  onClose,
}: {
  organizationId: string;
  onClose: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');

  const users = useQuery({
    queryKey: queryKeys.platform.orgUsers(organizationId),
    queryFn: ({ signal }) => getPlatformClient().listOrganizationUsers(organizationId, signal),
  });

  const start = useMutation({
    mutationFn: (targetUserId: string) =>
      getPlatformClient().startImpersonation({ organizationId, targetUserId }),
    onSuccess: (res) => {
      // Unmount the picker BEFORE touching the cache — otherwise
      // queryClient.clear() triggers a refetch of this dialog's own
      // org-users query mid-redirect, flashing empty → loading → list.
      onClose();
      // Principal just changed — clear ALL cached queries so the impersonated
      // user sees fresh data, not stale platform responses.
      queryClient.clear();
      toast.success('Impersonation started');
      navigate({ to: res.redirectTo });
    },
    onError: () => {
      toast.error('Failed to start impersonation');
    },
  });

  const filtered = (users.data ?? []).filter((u) => {
    const needle = filter.toLowerCase().trim();
    if (!needle) return true;
    return (
      u.email.toLowerCase().includes(needle) ||
      u.firstName.toLowerCase().includes(needle) ||
      u.lastName.toLowerCase().includes(needle)
    );
  });

  const startingUserId = start.isPending ? (start.variables as string) : null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        // Ignore close attempts while the mutation is in flight — prevents
        // users from dismissing mid-request and ending up in an
        // indeterminate state.
        if (!open && !start.isPending) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Pick a user to impersonate</DialogTitle>
          <DialogDescription>
            All of this user's actions will be double-logged with your platform identity.
          </DialogDescription>
        </DialogHeader>
        <Input
          placeholder="Search by name or email…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          autoFocus
          disabled={start.isPending}
        />
        <div className="max-h-96 overflow-y-auto space-y-1">
          {users.isLoading && <div className="text-muted-foreground text-sm">Loading users…</div>}
          {users.isError && <div className="text-destructive text-sm">Failed to load users.</div>}
          {filtered.map((u) => {
            const isStarting = startingUserId === u.id;
            return (
              <button
                key={u.id}
                onClick={() => start.mutate(u.id)}
                disabled={start.isPending}
                className="flex w-full items-center justify-between gap-2 rounded border px-3 py-2 text-left text-sm hover:bg-accent disabled:opacity-60"
              >
                <div className="min-w-0">
                  <div className="font-medium">
                    {u.firstName} {u.lastName}
                  </div>
                  <div className="text-muted-foreground text-xs">{u.email}</div>
                </div>
                {isStarting && (
                  <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" aria-hidden="true" />
                )}
              </button>
            );
          })}
          {!users.isLoading && filtered.length === 0 && (
            <div className="text-muted-foreground text-sm text-center py-4">No matches.</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={start.isPending}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
